import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactElement } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import {
  MetadataFieldTypes,
  MetadataUserListFieldSchema,
} from "@stw/yup-validators";

import {
  MetadataUserListFieldFragment,
  UpdateMetadataUserListFieldMutation,
  UpdateMetadataUserListFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import { handlePostSubmit } from "~frontend/components/campaignMetadata/common";
import {
  UPDATE_METADATA_USER_LIST_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type UpdateUserListFieldProps = {
  editField?: MetadataUserListFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormOutput = FieldValues & z.infer<typeof MetadataUserListFieldSchema>;

export function EditUserListFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: UpdateUserListFieldProps): ReactElement {
  const id = editField ? editField.id : undefined;

  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(MetadataUserListFieldSchema),
    values: {
      id,
      name: initialName,
      required: editField?.required || false,
    },
  });

  const [updateField] = useMutation<
    UpdateMetadataUserListFieldMutation,
    UpdateMetadataUserListFieldMutationVariables
  >(UPDATE_METADATA_USER_LIST_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (values: FormOutput) => {
    const { name, required } = values;
    const { errors, data } = await updateField({
      variables: {
        input: {
          id,
          name,
          required,
        },
      },
    });

    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataUserListField,
      errors,
    });
  };

  const onFormCancelClick = () => {
    reset();
    onCancelClick();
  };

  return (
    <FieldForm
      editField={editField}
      getValues={getValues}
      watch={watch}
      onTypeChange={onTypeChange}
      currentFieldType={MetadataFieldTypes.USER_LIST}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
    />
  );
}
